import { AnimationEvent } from '@angular/animations';
import { CdkAccordion, CdkAccordionItem } from '@angular/cdk/accordion';
import { UniqueSelectionDispatcher } from '@angular/cdk/collections';
import { CdkStepper } from '@angular/cdk/stepper';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  HostBinding,
  Inject,
  InjectionToken,
  Input,
  Optional,
  Output,
  SkipSelf,
} from '@angular/core';
import { Observable, takeUntil } from 'rxjs';
import { luiExpansionAnimations } from './expansion-animations';
import { ExpansionPanelHeaderComponent } from './expansion-panel-header.component';

/** ExpansionPanel's states. */
export type ExpansionPanelState = 'expanded' | 'collapsed';

export const CDK_ACCORDION = new InjectionToken<CdkAccordion>('CdkAccordion');
export const CDK_STEPPER = new InjectionToken<CdkStepper>('CdkStepper');

@Component({
    selector: 'lui-expansion-panel',
    templateUrl: './expansion-panel.component.html',
    animations: [luiExpansionAnimations.bodyExpansion],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ExpansionPanelComponent
  extends CdkAccordionItem
  implements AfterContentInit
{
  @HostBinding('class.lui-expansion-panel-header-trigger-disabled')
  @Input()
  headerTriggerDisabled = false;

  @Input() stepperTriggerDisabled = false;

  @HostBinding('class') hostClass = 'lui-expansion-panel';

  @HostBinding('class.lui-expansion-panel-disabled') get disabledClass() {
    return this.disabled || this.stepperTriggerDisabled;
  }

  @ContentChild(ExpansionPanelHeaderComponent)
  header!: ExpansionPanelHeaderComponent;

  private _hasStepper = false;

  @Output() animationComplete = new EventEmitter<AnimationEvent>();

  headerClicked!: Observable<void>;

  private _cd!: ChangeDetectorRef;

  constructor(
    @Optional() @SkipSelf() stepper: CdkStepper,
    @Optional() @SkipSelf() @Inject(CDK_ACCORDION) accordion: CdkAccordion,
    _changeDetectorRef: ChangeDetectorRef,
    _selectionDispatcher: UniqueSelectionDispatcher
  ) {
    super(accordion, _changeDetectorRef, _selectionDispatcher);
    this._cd = _changeDetectorRef;

    if (stepper) {
      this._hasStepper = true;
    }
  }

  ngAfterContentInit(): void {
    if (this.header) {
      this.headerClicked = this.header.headerClicked;

      this.headerClicked.pipe(takeUntil(this.destroyed)).subscribe(() => {
        if (!this.headerTriggerDisabled) {
          this.toggle();
        }
      });
    }
  }

  override toggle() {
    if (!this.disabled && !this._hasStepper) {
      this.expanded = !this.expanded;
    }
  }

  toggleStepperDisabled(value = !this.stepperTriggerDisabled): void {
    this.stepperTriggerDisabled = value;
    this._cd.markForCheck();
  }

  /** Gets the expanded state string. */
  _getExpandedState(): ExpansionPanelState {
    return this.expanded ? 'expanded' : 'collapsed';
  }
}

@Component({
    selector: 'lui-expansion-panel-content',
    template: '<ng-content></ng-content>',
    standalone: false
})
export class ExpansionPanelContentComponent {
  @HostBinding('class') hostClass = 'lui-expansion-panel-content';
}
